import React, { useEffect, useState } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../types/navigation';
import { useTheme } from '../theme/ThemeContext';
import { useWork } from '../context/WorkContext';
import { useAuth } from '../context/AuthContext';
import { Job } from '../types/work';
import ThemedText from '../components/common/ThemedText';
import EmptyState from '../components/work/EmptyState';

type MyProductsNavigationProp = StackNavigationProp<RootStackParamList>;
type MyProductsRouteProp = RouteProp<RootStackParamList, 'MyProducts'>;

const MyProductsScreen: React.FC = () => {
  const navigation = useNavigation<MyProductsNavigationProp>();
  const route = useRoute<MyProductsRouteProp>();
  const { theme } = useTheme();
  const { myJobs, fetchMyJobs, fetchUserJobs } = useWork();
  const { user } = useAuth();
  
  const targetUserId = route.params?.userId;
  const isOwnProducts = !targetUserId || targetUserId === user?.userId;
  
  const [loading, setLoading] = useState(true);
  const [userProducts, setUserProducts] = useState<Job[]>([]);

  useEffect(() => {
    loadMyProducts();
  }, [targetUserId]);

  const loadMyProducts = async () => {
    try {
      setLoading(true);
      if (isOwnProducts) {
        await fetchMyJobs();
      } else if (targetUserId) {
        const jobs = await fetchUserJobs(targetUserId);
        setUserProducts(jobs);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter only products from the appropriate jobs list
  const products = isOwnProducts 
    ? myJobs.filter(job => job.type === 'product')
    : userProducts.filter(job => job.type === 'product');

  const handleProductPress = (productId: string) => {
    navigation.navigate('MainTabs', {
      screen: 'Work',
      params: {
        screen: 'ProductDetail',
        params: { productId },
      },
    });
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const renderProduct = ({ item }: { item: Job }) => (
    <TouchableOpacity
      style={[styles.productCard, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={() => handleProductPress(item.id)}
      activeOpacity={0.7}
    >
      <View style={styles.productInfo}>
        <ThemedText style={[styles.productTitle, { color: theme.text }]} numberOfLines={2}>
          {item.title}
        </ThemedText>
        <ThemedText style={[styles.productCategory, { color: theme.textSecondary }]}>
          {item.category}
        </ThemedText>
        {item.price && (
          <ThemedText style={[styles.productPrice, { color: theme.accent }]}>
            ${item.price.toLocaleString()}
          </ThemedText>
        )}
        <View style={styles.statusRow}>
          <View style={[
            styles.statusBadge,
            { backgroundColor: item.status === 'open' ? theme.success + '20' : theme.disabled + '20' }
          ]}>
            <ThemedText style={[
              styles.statusText,
              { color: item.status === 'open' ? theme.success : theme.textSecondary }
            ]}>
              {item.status === 'open' ? 'Active' : item.status === 'sold' ? 'Sold' : 'Closed'}
            </ThemedText>
          </View>
        </View>
      </View>
      <Ionicons name="chevron-forward" size={24} color={theme.textSecondary} />
    </TouchableOpacity>
  );

  return (
    <SafeAreaView 
      style={[styles.container, { backgroundColor: theme.background }]} 
      edges={Platform.OS === 'ios' ? ['top', 'bottom'] : ['top', 'left', 'right', 'bottom']}
    >
      <View style={[styles.header, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {isOwnProducts ? 'My Products' : 'Products'}
        </ThemedText>
        <View style={styles.headerRight} />
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      ) : products.length === 0 ? (
        <EmptyState 
          icon="cube-outline" 
          message="No products yet. Your posted products will appear here"
        />
      ) : (
        <FlatList
          data={products}
          renderItem={renderProduct}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={true}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  headerRight: {
    width: 44,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 16,
    paddingBottom: 32,
  },
  productCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  productInfo: {
    flex: 1,
    marginRight: 12,
  },
  productTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  productCategory: {
    fontSize: 14,
    marginBottom: 8,
  },
  productPrice: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default MyProductsScreen;
